import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Award, Activity, RefreshCw, FileBadge, Download, Loader2, AlertCircle, CheckCircle, XCircle,
  Clock, Send, ArrowRight, ArrowLeft, X, ShieldCheck, CreditCard,
} from 'lucide-react';
import { cardCertificationApi, saveBlob } from '../../services/iso8583Api';
import { PageHeader, Card } from './common';
import { decisionLabel, finalizeBlocker, verdictSummary } from '../../utils/certification';

const VERDICT = {
  PASS:             'bg-green-100 text-green-800',
  CONDITIONAL_PASS: 'bg-amber-100 text-amber-800',
  FAIL:             'bg-red-100 text-red-800',
  INCOMPLETE:       'bg-gray-200 text-gray-700',
};

const CASE_ICON = {
  PASS:    <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />,
  FAIL:    <XCircle className="w-4 h-4 text-red-500 shrink-0" />,
  BLOCKED: <Clock className="w-4 h-4 text-gray-400 shrink-0" />,
  NOT_RUN: <Clock className="w-4 h-4 text-gray-300 shrink-0" />,
};

// Mandatory cases by who starts them, then optional, then the one that cannot run yet.
const GROUPS = [
  {
    key: 'acquirer', icon: ArrowRight,
    title: 'Mandatory — as acquirer (your gateway → the switch)',
    blurb: 'Driven by what you send. The amount chooses the scenario — see Test Cards.',
    match: (c) => c.obligation === 'MANDATORY' && c.kind === 'OBSERVED',
  },
  {
    key: 'issuer', icon: ArrowLeft,
    title: 'Mandatory — as issuer (the switch → your gateway)',
    blurb: 'Sign on, then press Send to my link: the switch sends your link a request, a reversal or an echo, and scores your answer.',
    match: (c) => c.obligation === 'MANDATORY' && c.kind === 'TRIGGERED',
  },
  {
    key: 'optional', icon: CheckCircle,
    title: 'Optional',
    blurb: 'A failed optional case makes the verdict a conditional pass; one never run does not count against you.',
    match: (c) => c.obligation === 'OPTIONAL',
  },
  {
    key: 'blocked', icon: Clock,
    title: 'Blocked — waiting on Sandbox infrastructure',
    blurb: 'Excluded from the score rather than failed.',
    match: (c) => c.kind === 'BLOCKED',
  },
];

/** How long the page keeps looking for a triggered case's verdict. */
const TRIGGER_WATCH_MS = 45000;

export default function CardCertification() {
  const [suite, setSuite] = useState(null);
  const [report, setReport] = useState(null);
  const [source, setSource] = useState('live');         // live | finalized
  const [latest, setLatest] = useState(null);           // last issued report, for the certificate
  const [loading, setLoading] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [triggers, setTriggers] = useState({});         // caseId -> { busy, message, started }
  const [selected, setSelected] = useState(null);
  const watchRef = useRef(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [prog, last] = await Promise.all([
        cardCertificationApi.progress(),
        cardCertificationApi.latestReport().catch(() => null),
      ]);
      setLatest(last);
      // Just after a finalize the open run is empty; the issued report is the useful view then.
      const exercised = (prog.cases || []).some((c) => c.status === 'PASS' || c.status === 'FAIL');
      if (!exercised && last) {
        setReport(last); setSource('finalized');
      } else {
        setReport(prog); setSource('live');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cardCertificationApi.getSuite().then(setSuite).catch((e) => setError(e.response?.data?.message || e.message));
    load();
    return () => clearInterval(watchRef.current);
  }, [load]);

  // The report carries the status; the suite carries the steps and whether the switch starts the case.
  const cases = useMemo(() => {
    const bySuite = Object.fromEntries((suite?.cases || []).map((c) => [c.caseId, c]));
    return (report?.cases || []).map((c) => ({ ...bySuite[c.caseId], ...c, kind: bySuite[c.caseId]?.kind }));
  }, [suite, report]);

  const trigger = async (caseId) => {
    setTriggers((t) => ({ ...t, [caseId]: { busy: true } }));
    try {
      const r = await cardCertificationApi.trigger(caseId);
      setTriggers((t) => ({ ...t, [caseId]: { busy: false, started: r.started, message: r.message } }));
      if (r.started) {
        // Watch progress until the verdict lands, or the switch's own time-out passes.
        const before = cases.find((c) => c.caseId === caseId)?.status;
        const until = Date.now() + TRIGGER_WATCH_MS;
        clearInterval(watchRef.current);
        watchRef.current = setInterval(async () => {
          const prog = await cardCertificationApi.progress().catch(() => null);
          const now = prog?.cases?.find((c) => c.caseId === caseId);
          if (prog && (now?.status !== before || Date.now() > until)) {
            clearInterval(watchRef.current);
            setReport(prog); setSource('live');
          }
        }, 2000);
      }
    } catch (e) {
      setTriggers((t) => ({ ...t, [caseId]: { busy: false, started: false, message: e.response?.data?.message || e.message } }));
    }
  };

  // Why the run cannot be finalized yet; the engine applies the same rule.
  const blocker = source === 'live' ? finalizeBlocker(report) : null;

  const finalize = async () => {
    if (!window.confirm(`Finalize and issue the signed certification report?\n\n${verdictSummary(report)}.\n\n`
      + 'This closes the current run and sends its report to an AfricaNenda authorising officer. '
      + 'Evidence you send afterwards starts a new one.')) return;
    setFinalizing(true); setError(null);
    try {
      const issued = await cardCertificationApi.finalize();
      setReport(issued); setLatest(issued); setSource('finalized');
      setNotice(`Report ${issued.reportId} issued. An AfricaNenda authorising officer reviews it before a certificate is issued.`);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setFinalizing(false);
    }
  };

  const downloadCertificate = async () => {
    try {
      saveBlob(await cardCertificationApi.downloadCertificate());
    } catch {
      setNotice('The certificate is not available yet — it is issued once your finalized report is approved.');
    }
  };

  const approved = ['APPROVED', 'APPROVED_WITH_CONDITIONS'].includes(latest?.signOff?.decision);
  const sc = report?.scorecard;

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader icon={Award} title="Certification">
        The AfricaNenda ISO 8583 suite: 21 cases showing that your gateway can connect to a card switch, act as
        acquirer and as issuer, and recover when a message is lost. It issues a signed, tamper-evident report;
        an AfricaNenda authorising officer's approval turns it into a certificate.
      </PageHeader>

      <Card title="How certification works">
        <ol className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            ['1 · Connect', <>Open a TCP link to the card switch and sign on with your institution id. <Link to="/iso8583/getting-started" className="text-primary-600 hover:underline">Getting Started</Link></>],
            ['2 · Exercise', <>As acquirer, send transactions — the amount picks the scenario (<Link to="/iso8583/test-cards" className="text-primary-600 hover:underline">Test Cards</Link>). As issuer, press <em>Send to my link</em> and answer what arrives.</>],
            ['3 · Certify', 'When the mandatory cases pass, Finalize & issue. The latest outcome of each case is what counts.'],
          ].map(([step, text]) => (
            <li key={step} className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-semibold text-primary-700">{step}</p>
              <p className="text-xs text-gray-600 mt-1">{text}</p>
            </li>
          ))}
        </ol>
      </Card>

      {/* Progress and actions */}
      <Card>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary-600" />
              {source === 'live' ? 'Live certification progress' : 'Last issued report'}
            </h2>
            {report && (
              <p className="text-xs text-gray-500 mt-1 font-mono">{report.reportId}</p>
            )}
            {blocker && (
              <p className="text-xs text-amber-700 mt-1 max-w-md">Not ready to finalize: {blocker}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={load} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
            </button>
            <button onClick={finalize} disabled={finalizing || loading || source !== 'live' || Boolean(blocker)}
              title={source === 'live' ? blocker || undefined : undefined}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {finalizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileBadge className="w-4 h-4" />} Finalize &amp; issue
            </button>
            {approved && (
              <button onClick={downloadCertificate}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-green-300 bg-green-50 text-sm font-medium text-green-800 hover:bg-green-100">
                <Download className="w-4 h-4" /> Certificate
              </button>
            )}
          </div>
        </div>

        {report && sc && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <div className="rounded-lg p-3 text-center bg-gray-50">
              <span className={`inline-block text-sm font-semibold px-2.5 py-1 rounded ${VERDICT[report.status] || VERDICT.INCOMPLETE}`}>
                {String(report.status).replace('_', ' ')}
              </span>
              <p className="text-xs text-gray-500 mt-1">Verdict</p>
            </div>
            {[
              ['Mandatory', `${sc.mandatory.passed}/${sc.mandatory.total}`],
              ['Optional', `${sc.optional.passed}/${sc.optional.total}`],
              ['Pass rate', `${Math.round(sc.overallPassRate * 1000) / 10}%`],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg p-3 text-center bg-gray-50">
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        )}

        {latest?.signOff && (
          <div className={`mt-4 text-sm rounded-lg px-3 py-2 border flex items-center gap-2 ${
            approved ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
            <ShieldCheck className="w-4 h-4 shrink-0" />
            <span>
              {latest.reportId}: {decisionLabel(latest.signOff.decision).toLowerCase()} by {latest.signOff.authorisingOfficer}
              {latest.signOff.decisionAt ? ` on ${latest.signOff.decisionAt.slice(0, 10)}` : ''}
              {latest.signOff.conditions?.length > 0 && <> — {latest.signOff.conditions[0].condition}</>}
              {latest.signOff.decision === 'REVOKED' && <>. Finalize a new run to be certified again.</>}
            </span>
          </div>
        )}
        {notice && <div className="mt-3 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">{notice}</div>}
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Cases */}
      {GROUPS.map((g) => {
        const inGroup = cases.filter((c) => (g.key === 'blocked' ? g.match(c) : c.kind !== 'BLOCKED' && g.match(c)));
        if (inGroup.length === 0) return null;
        const Icon = g.icon;
        return (
          <Card key={g.key}>
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Icon className="w-4 h-4 text-primary-600" /> {g.title}
            </h2>
            <p className="text-xs text-gray-500 mt-1 mb-3">{g.blurb}</p>
            <div className="divide-y divide-gray-100">
              {inGroup.map((c) => {
                const t = triggers[c.caseId];
                const observed = c.assertions?.find((a) => a.observed)?.observed;
                return (
                  <div key={c.caseId} className="py-2.5 flex items-start gap-3">
                    {CASE_ICON[c.status] || CASE_ICON.NOT_RUN}
                    <button type="button" onClick={() => setSelected(c)} className="flex-1 text-left min-w-0">
                      <p className="text-sm text-gray-900">
                        <span className="font-mono text-xs text-gray-400 mr-2">{c.caseId}</span>{c.title}
                      </p>
                      {observed && c.status !== 'BLOCKED' && (
                        <p className={`text-xs mt-0.5 ${c.status === 'FAIL' ? 'text-red-700' : 'text-gray-500'}`}>{observed}</p>
                      )}
                      {t?.message && (
                        <p className={`text-xs mt-0.5 ${t.started ? 'text-blue-700' : 'text-amber-700'}`}>{t.message}</p>
                      )}
                    </button>
                    {c.kind === 'TRIGGERED' && source === 'live' && (
                      <button type="button" onClick={() => trigger(c.caseId)} disabled={t?.busy}
                        className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-primary-200 text-xs font-medium text-primary-700 hover:bg-primary-50 disabled:opacity-50">
                        {t?.busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send to my link
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}

      {selected && <CaseDetail c={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

/** One case: what it proves, how to exercise it, and what was last seen. */
function CaseDetail({ c, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true" aria-labelledby="case-title">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-xs text-gray-400">{c.caseId} · {c.obligation} · {c.direction === 'OUTBOUND' ? 'switch → you' : 'you → switch'}</p>
            <h3 id="case-title" className="text-lg font-semibold text-gray-900 mt-1">{c.title}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-5 mb-2">How to exercise it</h4>
        <ol className="list-decimal ml-5 space-y-1.5 text-sm text-gray-700">
          {(c.steps || []).map((s, i) => <li key={i}>{s}</li>)}
        </ol>

        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-5 mb-2">What passing means</h4>
        <ul className="space-y-2">
          {(c.assertions || []).map((a) => (
            <li key={a.id || a.desc} className="text-sm flex items-start gap-2">
              {CASE_ICON[a.status] || CASE_ICON.NOT_RUN}
              <div>
                <p className="text-gray-800">{a.desc}</p>
                {a.observed && <p className="text-xs text-gray-500 mt-0.5">Last seen: {a.observed}</p>}
              </div>
            </li>
          ))}
        </ul>
        {c.blockedReason && <p className="text-xs text-gray-500 mt-4">{c.blockedReason}</p>}
        <p className="text-xs text-gray-400 mt-5 flex items-center gap-1">
          <CreditCard className="w-3.5 h-3.5" /> Only test cards are ever processed; a real card number is refused with 14.
        </p>
      </div>
    </div>
  );
}
